import { HttpErrorResponse } from '@angular/common/http';
import { createFeature, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { GetOrganizationChecklistDto } from '../../../../../../../../aumico-lib-users-fe/src/public-api';
import { Task } from '../../domain/models/fcp.model';
import { ClosingEntry, DocumentLink } from '../models/fcp.model';
import { FcpTreeItem } from '../models/fcpTreeItem.model';
import * as fromActions from './fcp.actions';
export const ANNUAL_REPORT_FCP_FEATURE_KEY = 'aumicoAnnualReportFcp';

export interface FcpFeatureState {
    rootItem: FcpTreeItem | undefined;
    navigationItems: FcpTreeItem[] | undefined;
    activeNavigationItem: FcpTreeItem | undefined;
    testGroupItems: FcpTreeItem[] | undefined;
    activeTestGroupItem: FcpTreeItem | undefined;
    testItems: FcpTreeItem[] | undefined;
    activeTestItem: FcpTreeItem | undefined;
    fcpItemToReset: FcpTreeItem | undefined;
    fcpItemToUpdate: FcpTreeItem | undefined;
    showAdjustments: boolean;
    error: HttpErrorResponse | undefined;
    checklists: GetOrganizationChecklistDto[] | undefined;
}

export interface AppState {
    aumicoAnnualReportFcp: FcpFeatureState;
}

export const selectFeature = createFeatureSelector<FcpFeatureState>(ANNUAL_REPORT_FCP_FEATURE_KEY);

export const selectNavigationItems = createSelector(selectFeature, (state: FcpFeatureState) => state.navigationItems);
export const selectActiveNavigationItem = createSelector(
    selectFeature,
    (state: FcpFeatureState) => state.activeNavigationItem,
);
export const selectActiveTestGroupItem = createSelector(
    selectFeature,
    (state: FcpFeatureState) => state.activeTestGroupItem,
);
export const selectRootItem = createSelector(selectFeature, (state: FcpFeatureState) => state.rootItem);
export const selectActiveTestItem = createSelector(selectFeature, (state: FcpFeatureState) => state.activeTestItem);
export const selectFcpItemToReset = createSelector(selectFeature, (state: FcpFeatureState) => state.fcpItemToReset);
export const selectFcpItemToUpdate = createSelector(selectFeature, (state: FcpFeatureState) => state.fcpItemToUpdate);
export const selectShowAdjustments = createSelector(selectFeature, (state: FcpFeatureState) => state.showAdjustments);
export const hasContentToDisplay = createSelector(
    selectFeature,
    (state: FcpFeatureState) => state.activeTestGroupItem !== undefined || state.activeTestItem !== undefined,
);

export const selectFcpChecklist = createSelector(selectFeature, (state: FcpFeatureState) => state.checklists);

export const selectFliChecklists = (fli: string) =>
    createSelector(selectFcpChecklist, (entities) =>
        Object.values(entities).filter((checklist) => checklist.flis?.includes(fli)),
    );

// Selectors Declarations - END

// Reducer Declarations - START

export const initialState: FcpFeatureState = {
    error: undefined,
    rootItem: undefined,
    navigationItems: [],
    activeNavigationItem: undefined,
    activeTestGroupItem: undefined,
    activeTestItem: undefined,
    testGroupItems: [],
    testItems: [],
    fcpItemToReset: undefined,
    fcpItemToUpdate: undefined,
    showAdjustments: false,
    checklists: undefined,
};

export const fcpReducer = createReducer(
    initialState,
    on(fromActions.setRootItem, (state: FcpFeatureState, action) => ({
        ...state,
        rootItem: action.payload,
    })),
    on(fromActions.resetActiveItems, (state: FcpFeatureState, action) => ({
        ...state,
        activeNavigationItem: null,
        activeTestGroupItem: null,
        activeTestItem: null,
        fcpItemToReset: undefined,
        fcpItemToUpdate: undefined,
    })),
    on(fromActions.setNavigationItemsSuccess, (state: FcpFeatureState, action) => ({
        ...state,
        navigationItems: action.payload,
    })),
    on(fromActions.setActiveNavigationSuccess, (state: FcpFeatureState, action) => ({
        ...state,
        activeNavigationItem: action.payload,
    })),
    on(fromActions.toggleActiveTestGroupItemSuccess, (state: FcpFeatureState, action) => ({
        ...state,
        activeTestGroupItem: action.payload,
    })),
    on(fromActions.setActiveTestItemSuccess, (state: FcpFeatureState, action) => ({
        ...state,
        activeTestItem: action.payload,
    })),
    on(fromActions.setFcpItemToReset, (state: FcpFeatureState, action) => ({
        ...state,
        fcpItemToReset: action.payload,
    })),
    on(fromActions.setShowAdjustments, (state: FcpFeatureState, action) => ({
        ...state,
        showAdjustments: action.payload,
    })),
    on(fromActions.addComment, (state, action) => {
        const newComment = action.payload;
        const activeTestItem = {
            ...state.activeTestItem,
            comments: [...state.activeTestItem.comments, newComment],
        };
        return {
            ...state,
            activeTestItem,
        };
    }),
    on(fromActions.updateComment, (state, action) => {
        const updatedComment = action.payload;
        const activeTestItem = {
            ...state.activeTestItem,
            comments: state.activeTestItem.comments.map((c) =>
                c.id === updatedComment.id ? { ...c, ...updatedComment } : c,
            ),
        };
        return {
            ...state,
            activeTestItem,
        };
    }),
    on(fromActions.deleteComment, (state, action) => {
        const deletedCommentId = action.payload.id;
        const activeTestItem = {
            ...state.activeTestItem,
            comments: state.activeTestItem.comments.filter((c) => c.id !== deletedCommentId),
        };
        return {
            ...state,
            activeTestItem,
        };
    }),
    on(fromActions.addClosingEntry, (state, action) => {
        const newClosingEntry = action.payload;
        const activeTestItem: FcpTreeItem = {
            ...state.activeTestItem,
            closingEntries: [...state.activeTestItem.closingEntries, newClosingEntry],
        };
        return {
            ...state,
            activeTestItem,
        };
    }),
    on(fromActions.updateClosingEntry, (state, action) => {
        const updatedClosingEntry = action.payload;

        if (state.activeTestItem) {
            const activeTestItem: FcpTreeItem = {
                ...state.activeTestItem,
                closingEntries: state.activeTestItem.closingEntries.map((c) =>
                    c.id === updatedClosingEntry.id ? { ...c, ...updatedClosingEntry } : c,
                ),
            };
            return {
                ...state,
                activeTestItem,
                fcpItemToUpdate: activeTestItem,
            };
        } else {
            const { updatedTree, updatedItem } = updateClosingEntryInTree(state.rootItem, updatedClosingEntry);
            return {
                ...state,
                rootItem: updatedTree,
                fcpItemToUpdate: updatedItem,
            };
        }
    }),
    on(fromActions.deleteClosingEntry, (state, action) => {
        const deletedClosingEntryId = action.payload.id;

        if (state.activeTestItem) {
            const activeTestItem = {
                ...state.activeTestItem,
                closingEntries: state.activeTestItem.closingEntries.filter((c) => c.id !== deletedClosingEntryId),
            };
            return {
                ...state,
                activeTestItem,
                fcpItemToUpdate: activeTestItem, // Update if necessary
            };
        } else {
            const { updatedTree, updatedItem } = deleteClosingEntryInTree(state.rootItem, deletedClosingEntryId);
            return {
                ...state,
                rootItem: updatedTree,
                fcpItemToUpdate: updatedItem, // Update if necessary
            };
        }
    }),

    on(fromActions.updateChecklist, (state, action) => {
        const { tasks, checklistName } = action;
        const activeTestItem: FcpTreeItem = {
            ...state.activeTestItem,
            tasks: cloneDeep(tasks),
            checklistname: checklistName,
        };

        const children = cloneDeep(state.activeTestGroupItem?.children);
        const activeChildIndex = children.findIndex((child) => child.name === activeTestItem.name);
        children[activeChildIndex] = activeTestItem;

        const activeTestGroupItem: FcpTreeItem = {
            ...state.activeTestGroupItem,
            children,
            tasks: state.activeTestGroupItem?.tasks.map((task) => ({
                ...task,
                isDone: children?.every((child) => {
                    const childTask = child.tasks.find((t) => t.text === task.text);

                    return childTask?.isDone;
                }),
            })),
        };

        return {
            ...state,
            activeTestItem,
            activeTestGroupItem,
        };
    }),

    on(fromActions.createChecklistForTestGroup, (state, action) => {
        const { tasks, checklistName } = action;

        const activeTestGroupItem: FcpTreeItem = {
            ...state.activeTestGroupItem,
            tasks: cloneDeep(tasks),
            checklistname: checklistName,
            children: state.activeTestGroupItem?.children.map((child) => {
                if (child.tasks?.length) {
                    const doneTasks = child.tasks?.filter((task) => task.isDone);
                    const nonCommonTasks = tasks.filter(
                        (task) => !child.tasks?.some((childTask) => childTask.text === task.text),
                    );
                    return {
                        ...child,
                        checklistname: checklistName,
                        tasks: [...cloneDeep(doneTasks), ...cloneDeep(nonCommonTasks)],
                    };
                }

                return {
                    ...child,
                    checklistname: checklistName,
                    tasks: cloneDeep(tasks),
                };
            }),
        };

        return {
            ...state,
            activeTestGroupItem,
        };
    }),

    on(fromActions.updateTasksForTestGroupAndItsChildren, (state, action) => {
        const { updatedTasks, createdTasks, deletedTasks } = action;
        const activeTestGroupItemTasks = [...updatedTasks, ...createdTasks];

        const children = state.activeTestGroupItem?.children?.map((child) => {
            const tasksWithoutDeletedItems = child.tasks.filter(
                (task) => !deletedTasks.includes(task.text) || task.isDone,
            );

            const childTasks = mergeTasksByName(tasksWithoutDeletedItems, updatedTasks);

            return { ...child, tasks: [...cloneDeep(childTasks), ...cloneDeep(createdTasks)] };
        });

        const activeTestGroupItem: FcpTreeItem = {
            ...state.activeTestGroupItem,
            tasks: activeTestGroupItemTasks,
            children,
        };

        return {
            ...state,
            activeTestGroupItem,
        };
    }),

    on(fromActions.markAllTasksAsCompleted, (state, action) => {
        const { doneBy, doneByName, task } = action;

        const taskToUpdateIndex = state.activeTestGroupItem?.tasks?.findIndex((taskToMark) => taskToMark.text === task);
        const stateTasks = [...state.activeTestGroupItem?.tasks];
        stateTasks[taskToUpdateIndex] = {
            ...stateTasks[taskToUpdateIndex],
            isDone: true,
            doneAt: new Date(),
            doneBy,
            doneByName,
        };

        const children = state.activeTestGroupItem?.children?.map((child) => {
            const taskToUpdateIndex = child.tasks.findIndex((taskToMark) => taskToMark.text === task);

            const updatedChildTasks = [...child.tasks];
            updatedChildTasks[taskToUpdateIndex] = {
                ...updatedChildTasks[taskToUpdateIndex],
                isDone: true,
                doneAt: new Date(),
                doneBy,
                doneByName,
            };

            return { ...child, tasks: cloneDeep(updatedChildTasks) };
        });

        const activeTestGroupItem: FcpTreeItem = {
            ...state.activeTestGroupItem,
            children,
            tasks: stateTasks,
        };

        return {
            ...state,
            activeTestGroupItem,
        };
    }),

    on(fromActions.deleteChecklist, (state) => {
        const children = state.activeTestGroupItem?.children?.map((child) => {
            const completedTasks = child.tasks
                .filter((task) => task.isDone)
                .map((task) => ({ ...task, isSealed: true }));

            return {
                ...child,
                tasks: cloneDeep(completedTasks),
                checklistname: '',
            };
        });
        const completedTasks = state.activeTestItem?.tasks
            ?.filter((task) => task.isDone)
            .map((task) => ({ ...task, isSealed: true }));

        return {
            ...state,
            activeTestItem: {
                ...state.activeTestItem,
                tasks: cloneDeep(completedTasks),
            },
            activeTestGroupItem: {
                ...state.activeTestGroupItem,
                children,
                tasks: [],
                isDefaultChecklistDeleted: true,
            },
        };
    }),

    on(fromActions.financialClosingProcessItemDocument.success, (state, action) => {
        // if user left the page before upload is done
        if (!state.activeTestItem) {
            return { ...state };
        }
        const { name, scanStatus, uuid, createdBy, size } = action.payload;
        const updatedDocuments: DocumentLink[] = cloneDeep<DocumentLink[]>(state.activeTestItem.documents);
        const fileIndex = updatedDocuments.findIndex((file) => file.name === name && !file.fileId);

        // if user switched to another item before upload is done
        if (fileIndex === -1) {
            return {
                ...state,
                activeTestItem: {
                    ...state.activeTestItem,
                    documents: [...updatedDocuments],
                },
            };
        }

        updatedDocuments[fileIndex] = {
            ...updatedDocuments[fileIndex],
            fileId: uuid,
            status: scanStatus,
            testGroup: state.activeTestGroupItem?.name,
            testItem: state.activeTestItem.name,
            createdAt: new Date(),
            createdBy: createdBy,
            id: uuid,
            uri: '',
            size,
        };

        return {
            ...state,
            activeTestItem: {
                ...state.activeTestItem,
                documents: [...updatedDocuments],
            },
        };
    }),

    on(fromActions.financialClosingProcessItemDocument.markfordeletionfcpdocuments, (state, action) => {
        const fileId = action.payload;
        const updatedDocuments: DocumentLink[] = cloneDeep<DocumentLink[]>(state.activeTestItem.documents);
        const fileIndex = updatedDocuments.findIndex((doc) => doc.fileId === fileId);
        updatedDocuments.splice(fileIndex, 1);

        return {
            ...state,
            activeTestItem: {
                ...state.activeTestItem,
                documents: [...updatedDocuments],
            },
        };
    }),

    on(fromActions.financialClosingProcessItemDocument.do, (state, action) => {
        const documents = cloneDeep(state.activeTestItem.documents);
        const { name, uploadedByName, dueDate } = action.fileMetaData;

        documents.push({
            name,
            uploadedByName,
            dueDate,
        } as DocumentLink);

        return {
            ...state,
            activeTestItem: {
                ...state.activeTestItem,
                documents,
            },
        };
    }),

    on(fromActions.getFcpChecklist.loadChecklistsSuccess, (state, action) => {
        return {
            ...state,
            checklists: action.checklists,
        };
    }),
);

export const FcpFeature = createFeature({
    name: ANNUAL_REPORT_FCP_FEATURE_KEY,
    reducer: fcpReducer,
});

export const { name, reducer, selectError } = FcpFeature;

function updateClosingEntryInTree(
    item: FcpTreeItem,
    updatedClosingEntry: ClosingEntry,
): { updatedTree: FcpTreeItem; updatedItem?: FcpTreeItem } {
    // Check if the current item has the closing entry we need to update
    let closingEntries = item.closingEntries || [];
    const hasClosingEntry = closingEntries.some((c) => c.id === updatedClosingEntry.id);

    if (hasClosingEntry) {
        // Update the closing entry in the current item
        const updatedClosingEntries = closingEntries.map((c) =>
            c.id === updatedClosingEntry.id ? { ...c, ...updatedClosingEntry } : c,
        );
        const updatedItem = { ...item, closingEntries: updatedClosingEntries };
        return { updatedTree: updatedItem, updatedItem };
    } else if (item.children && item.children.length > 0) {
        // Recursively search in the children
        let foundItem: FcpTreeItem | undefined;
        const updatedChildren = item.children.map((child) => {
            const { updatedTree: updatedChild, updatedItem } = updateClosingEntryInTree(child, updatedClosingEntry);
            if (updatedItem) {
                foundItem = updatedItem;
            }
            return updatedChild;
        });
        const updatedTree = { ...item, children: updatedChildren };
        return { updatedTree, updatedItem: foundItem };
    } else {
        // If no closing entry found and no children, return the item as is
        return { updatedTree: item };
    }
}

function deleteClosingEntryInTree(
    item: FcpTreeItem,
    deletedClosingEntryId: string,
): { updatedTree: FcpTreeItem; updatedItem?: FcpTreeItem } {
    // Check if the current item has the closing entry we need to delete
    let closingEntries = item.closingEntries || [];
    const hasClosingEntry = closingEntries.some((c) => c.id === deletedClosingEntryId);

    if (hasClosingEntry) {
        // Delete the closing entry from the current item
        const updatedClosingEntries = closingEntries.filter((c) => c.id !== deletedClosingEntryId);
        const updatedItem = { ...item, closingEntries: updatedClosingEntries };
        return { updatedTree: updatedItem, updatedItem };
    } else if (item.children && item.children.length > 0) {
        // Recursively search in the children
        let foundItem: FcpTreeItem | undefined;
        const updatedChildren = item.children.map((child) => {
            const { updatedTree: updatedChild, updatedItem } = deleteClosingEntryInTree(child, deletedClosingEntryId);
            if (updatedItem) {
                foundItem = updatedItem;
            }
            return updatedChild;
        });
        const updatedTree = { ...cloneDeep(item), children: updatedChildren };
        return { updatedTree, updatedItem: foundItem };
    } else {
        // If no closing entry found and no children, return the item as is
        return { updatedTree: item };
    }
}

const mergeTasksByName = (groupTasks: Task[], activeTasks: Task[]): Task[] => {
    const getKey = (task: Task) => task.id;

    const activeTaskMap = new Map(activeTasks.map((task) => [getKey(task), task]));
    const result: Task[] = [];

    groupTasks.forEach((groupTask) => {
        const key = getKey(groupTask);

        if (groupTask.isDone) {
            result.push(groupTask);
            activeTaskMap.delete(key);
        } else {
            const activeTask = activeTaskMap.get(key);
            if (activeTask) {
                if (activeTask.text !== groupTask.text) {
                    result.push({ ...activeTask, isDone: groupTask.isDone });
                } else {
                    result.push(activeTask);
                }
                activeTaskMap.delete(key);
            }
        }
    });

    result.push(...activeTaskMap.values());
    return result;
};
